/** Looks from the operator's game.html. Chosen in the lobby, drawn in the arena. */

export type WormPaint = 'solid' | 'stripe' | 'fade' | 'pulse';
export type WormTemplate = 'classic' | 'neural' | 'ribbon' | 'thorn' | 'pixel' | 'bubble';
export type EyeStyle = 'round' | 'wide' | 'sleepy' | 'sharp';

export type WormSkin = {
  id: string;
  a: string;
  b: string;
  paint: WormPaint;
  name: string;
};

export type WormLook = {
  a: string;
  b: string;
  paint: WormPaint;
  template: WormTemplate;
  /** Color of the body glow, shown only while boosting. */
  glow: string;
  eyes: EyeStyle;
};

export const GLOW_COLORS: Array<{ id: string; name: string; c: string }> = [
  { id: 'pink', name: 'Hot pink', c: '#ff2d95' },
  { id: 'cyan', name: 'Cyan', c: '#22e1ff' },
  { id: 'lime', name: 'Lime', c: '#9dff3c' },
  { id: 'violet', name: 'Violet', c: '#c026ff' },
  { id: 'gold', name: 'Gold', c: '#ffc23c' },
  { id: 'white', name: 'White', c: '#ffffff' },
];

export const EYE_STYLES: Array<{ id: EyeStyle; name: string }> = [
  { id: 'round', name: 'Round' },
  { id: 'wide', name: 'Wide' },
  { id: 'sleepy', name: 'Sleepy' },
  { id: 'sharp', name: 'Sharp' },
];

export const WORM_SKINS: WormSkin[] = [
  { id: 'hot-pink', a: '#ff2d95', b: '#ffb3d9', paint: 'solid', name: 'Hot pink' },
  { id: 'magenta', a: '#ff00c8', b: '#ff7ab8', paint: 'solid', name: 'Magenta' },
  { id: 'rose', a: '#ff7ab8', b: '#ffd1e8', paint: 'solid', name: 'Rose' },
  { id: 'blush', a: '#ffb3d9', b: '#fff0f8', paint: 'solid', name: 'Blush' },
  { id: 'ultraviolet', a: '#c026ff', b: '#ff7ab8', paint: 'solid', name: 'Ultraviolet' },
  { id: 'neon-cherry', a: '#ff1f5a', b: '#ff7ab8', paint: 'solid', name: 'Neon cherry' },
  { id: 'candy-stripe', a: '#ff2d95', b: '#fff0f8', paint: 'stripe', name: 'Candy stripe' },
  { id: 'sunset-fade', a: '#ff2d95', b: '#ffb36b', paint: 'fade', name: 'Sunset fade' },
  { id: 'ultra-pulse', a: '#c026ff', b: '#ff7ab8', paint: 'pulse', name: 'Ultra pulse' },
  { id: 'synapse-fade', a: '#ff00c8', b: '#8a2bff', paint: 'fade', name: 'Synapse fade' },
];

export const WORM_TEMPLATES: Array<{ id: WormTemplate; name: string }> = [
  { id: 'classic', name: 'Classic' },
  { id: 'neural', name: 'Neural' },
  { id: 'ribbon', name: 'Ribbon' },
  { id: 'thorn', name: 'Thorn' },
  { id: 'pixel', name: 'Pixel' },
  { id: 'bubble', name: 'Bubble' },
];

const SKIN_KEY = 'ztg-worm-skin';
const TEMPLATE_KEY = 'ztg-worm-template';
const GLOW_KEY = 'ztg-worm-glow';
const EYES_KEY = 'ztg-worm-eyes';

export function mixHex(a: string, b: string, k: number): string {
  const p = (h: string): [number, number, number] => [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ];
  const x = p(a);
  const y = p(b);
  const t = Math.max(0, Math.min(1, k));
  return `rgb(${x.map((v, i) => (v + (y[i]! - v) * t) | 0).join(',')})`;
}

export function segmentColor(look: WormLook, i: number, n: number, t: number): string {
  if (look.paint === 'stripe') return i % 6 < 3 ? look.a : look.b;
  if (look.paint === 'fade') return mixHex(look.a, look.b, n <= 1 ? 0 : i / n);
  if (look.paint === 'pulse') return mixHex(look.a, look.b, (Math.sin(t * 0.006 - i * 0.45) + 1) / 2);
  return look.a;
}

export function defaultLook(): WormLook {
  const skin = WORM_SKINS[0]!;
  return { a: skin.a, b: skin.b, paint: skin.paint, template: 'classic', glow: skin.a, eyes: 'round' };
}

export function selectedIds(): { skin: string; template: string; glow: string; eyes: EyeStyle } {
  let skin = WORM_SKINS[0]!.id;
  let template: WormTemplate = 'classic';
  let glow = WORM_SKINS[0]!.a;
  let eyes: EyeStyle = 'round';
  try {
    const savedSkin = localStorage.getItem(SKIN_KEY);
    const savedTemplate = localStorage.getItem(TEMPLATE_KEY);
    const savedGlow = localStorage.getItem(GLOW_KEY);
    const savedEyes = localStorage.getItem(EYES_KEY);
    if (savedSkin && WORM_SKINS.some((row) => row.id === savedSkin)) skin = savedSkin;
    if (savedTemplate && WORM_TEMPLATES.some((row) => row.id === savedTemplate)) template = savedTemplate as WormTemplate;
    if (savedGlow && GLOW_COLORS.some((row) => row.c === savedGlow)) glow = savedGlow;
    if (savedEyes && EYE_STYLES.some((row) => row.id === savedEyes)) eyes = savedEyes as EyeStyle;
  } catch { /* private mode */ }
  return { skin, template, glow, eyes };
}

export function loadLook(): WormLook {
  const base = defaultLook();
  try {
    const skin = WORM_SKINS.find((row) => row.id === localStorage.getItem(SKIN_KEY));
    const template = WORM_TEMPLATES.find((row) => row.id === localStorage.getItem(TEMPLATE_KEY));
    const glow = localStorage.getItem(GLOW_KEY);
    const eyes = localStorage.getItem(EYES_KEY);
    if (skin) {
      base.a = skin.a;
      base.b = skin.b;
      base.paint = skin.paint;
      base.glow = skin.a;
    }
    if (template) base.template = template.id;
    if (glow && GLOW_COLORS.some((row) => row.c === glow)) base.glow = glow;
    if (eyes && EYE_STYLES.some((row) => row.id === eyes)) base.eyes = eyes as EyeStyle;
  } catch { /* private mode */ }
  return base;
}

export function saveLook(skinId: string, templateId: string, glow: string, eyes: EyeStyle): WormLook {
  try {
    localStorage.setItem(SKIN_KEY, skinId);
    localStorage.setItem(TEMPLATE_KEY, templateId);
    localStorage.setItem(GLOW_KEY, glow);
    localStorage.setItem(EYES_KEY, eyes);
  } catch { /* private mode */ }
  return loadLook();
}

/** Stable look for another worm, so the field stays varied without a picker in the game. */
export function lookForId(id: string): WormLook {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 33 + id.charCodeAt(i)) >>> 0;
  const skin = WORM_SKINS[h % WORM_SKINS.length]!;
  const template = WORM_TEMPLATES[(h >>> 8) % WORM_TEMPLATES.length]!;
  const glow = GLOW_COLORS[(h >>> 13) % GLOW_COLORS.length]!;
  const eyes = EYE_STYLES[(h >>> 17) % EYE_STYLES.length]!;
  return { a: skin.a, b: skin.b, paint: skin.paint, template: template.id, glow: glow.c, eyes: eyes.id };
}
